import { CSSProperties, useContext, useEffect, useState } from 'react';
import React from 'react';
import { NumericFormat } from 'react-number-format';
import { useDispatch } from 'react-redux';
import Slider from '@mui/material/Slider';
import GameContext from 'contexts/GameContext';
import GlobalContext from 'contexts/GlobalContext';
import Decimal from 'decimal.js';
import { useTableSelector } from 'hooks/useTableSelector';
import { updateCurrentPlayerHasAddOnChips } from 'reducers/currentTable';
import useGameService from 'services/GameService';
import { calculateStepValue, numberFormatter } from 'utils/functions';

import GameModal, {
  btnRowStyle,
  primaryBtnStyle,
  secondaryBtnStyle
} from 'components/GameModal/GameModal';
import CountDownTimer from 'components/Timer/CountDownTimer';

const messageStyle: CSSProperties = {
  fontSize: '0.85rem',
  color: 'rgba(255, 255, 255, 0.6)',
  lineHeight: 1.5,
  textAlign: 'center'
};

const presetBtnStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.15rem',
  padding: '0.5rem',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '10px',
  background: 'rgba(255, 255, 255, 0.04)',
  cursor: 'pointer',
  fontFamily: 'inherit'
};

const disabledPresetBtnStyle: CSSProperties = {
  ...presetBtnStyle,
  opacity: 0.4,
  cursor: 'default'
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '0.75rem 1rem',
  fontSize: '1rem',
  fontWeight: 600,
  fontFamily: 'inherit',
  color: '#ffffff',
  backgroundColor: 'rgba(255, 255, 255, 0.08)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  borderRadius: '10px',
  outline: 'none'
};

// Stable module-level component so NumericFormat's `customInput` reference is
// identical across renders. Passing an inline arrow here (props => <input .../>)
// makes React see a NEW component type on every parent render — the input is
// unmounted + remounted per keystroke, and the user loses focus mid-typing.
// forwardRef is required so react-number-format can attach its internal ref
// (used to set the caret position after masking).
const AmountInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function AmountInput(props, ref) {
    return <input ref={ref} {...props} style={inputStyle} />;
  }
);

// Whole-string currency format accepted from the clipboard: optional "$", then
// either plain digits or correctly grouped thousands, then optional decimals.
const PASTED_AMOUNT_PATTERN = /^\$?(\d{1,3}(,\d{3})+|\d+)(\.\d+)?$/;

// Parse a pasted amount strictly. Stripping unexpected characters would turn
// malformed text into a different valid amount ("1e3" → 13), so anything that
// doesn't match yields NaN, which leaves Add Chips disabled.
const parsePastedAmount = (text: string): number => {
  const trimmed = text.trim();
  if (!PASTED_AMOUNT_PATTERN.test(trimmed)) return NaN;
  return Number(trimmed.replace(/[$,]/g, ''));
};

const AddChipsModal = () => {
  const game = useTableSelector((table) => table.game);
  const { bb, maximumTopUp, languageKey, isPennyTable, tablelow: gameTablelow } = game;
  const { seatId, isSeated: isPlayerSeated } = useTableSelector((table) => table.currentPlayer);
  const { rebuy, leaveTable } = useGameService();

  const { openAddChips, isTimerOff, setOpenAddChips, setIsTimerOff } = useContext(GameContext);
  const { closeBuyInModal, setCloseBuyInModal, setOpenWaitForBuyIn } = useContext(GlobalContext);
  const players = useTableSelector((table) => table.players);
  const [addChipsAmount, setAddChipsAmount] = useState(0);
  const tablelow = gameTablelow > maximumTopUp ? maximumTopUp : gameTablelow;
  const half = (Number(maximumTopUp) / 2).toFixed(2);
  const dispatch = useDispatch();
  const currentPlayerHasAddOnChips = (game as any)[`p${seatId}hasAddOnChips`];

  const sliderStep = isPennyTable ? calculateStepValue(bb) : 1;

  // Numeric Min preset. Must NOT round-trip through numberFormatter: it emits
  // locale commas, so +"1,000" is NaN and $1,000+ tables seeded a blank field
  // that still submitted (NaN fails both the < min and > max checks).
  // A cleared field reads as 0, so reject non-positive amounts outright —
  // otherwise a zero tablelow would let a blank field through the range check.
  const minAddChipsAmount = maximumTopUp <= 0 ? 0 : Number(tablelow);
  const isAddChipsAmountInvalid =
    maximumTopUp === 0 ||
    !Number.isFinite(addChipsAmount) ||
    addChipsAmount <= 0 ||
    addChipsAmount < tablelow ||
    addChipsAmount > maximumTopUp;

  const onComplete = () => {
    setCloseBuyInModal(true);
    setOpenAddChips(false);
    if (isPlayerSeated && players[seatId].stack < bb) {
      // leaveTable(false, true);
      // New requirement: stood up the player.
      // setTimeout(() => window.location.reload(), 100);
    }
  };

  // Default the add-chips amount to the table minimum whenever the dialog opens
  // instead of 0, mirroring the buy-in modal so a player can add the minimum
  // with a single tap rather than dragging the slider up from 0. When there's
  // no top-up headroom (maximumTopUp <= 0) the Min preset is 0, so match that.
  useEffect(() => {
    if (openAddChips) {
      setAddChipsAmount(minAddChipsAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAddChips, tablelow, maximumTopUp]);

  const handleAddChipsSlider = (e: Event) => {
    setAddChipsAmount(Number((e?.target as any).value));
  };

  const handleAddChips = () => {
    // A rejected amount must not consume the once-per-hand add-chips attempt.
    if (isAddChipsAmountInvalid) return;
    // Track if player rebuys within the wait period or after.
    // If within wait period, set pstatus = 0, else, trigger returnTable.
    rebuy(seatId, addChipsAmount, languageKey === 'GAME_MSG_BUSTED_PAUSE' ? true : false);
    setOpenAddChips(false);
    setIsTimerOff(false);
    setAddChipsAmount(0);
    setCloseBuyInModal(false);
    setOpenWaitForBuyIn(false);
    dispatch(updateCurrentPlayerHasAddOnChips(seatId));
  };

  const cancelAddChips = () => {
    setAddChipsAmount(0);
    setOpenAddChips(false);
    // Always restore the turn clock when the modal closes — otherwise a flow
    // that opened it with the timer paused (e.g. the busted-rebuy path) would
    // leave the player's seat timer hidden for the rest of the turn. The
    // busted-cancel branch below still reads the pre-update isTimerOff value
    // (state updates are async), so its leaveTable logic is unchanged.
    setIsTimerOff(false);

    if (languageKey === 'GAME_MSG_BUSTED_PAUSE' && isTimerOff) setCloseBuyInModal(true);
    else setCloseBuyInModal(false);

    if (
      languageKey === 'GAME_MSG_BUSTED_PAUSE' &&
      isTimerOff &&
      isPlayerSeated &&
      players[seatId].stack < bb
    ) {
      leaveTable(false, true);
      // New requirement: stood up the player.
      // setTimeout(() => window.location.reload(), 100);
    }
  };

  // Reads the RAW numeric value from react-number-format (onValueChange), not
  // the DOM change event. onChange's event.target.value is the FORMATTED string
  // — including the "$" prefix ("$12") — so Number("$12") is NaN and the field
  // silently stored strings in state, breaking submission. floatValue is the
  // parsed number ready for validation.
  //
  // Out-of-range entries are NOT clamped in the handler — the input shows what
  // the player typed, and the Add Chips button's disabled check (min/max)
  // below is what blocks submission. Snapping to a boundary silently was
  // confusing because it made typing feel like it was being partially eaten.
  const handleAddChipsValue = (values: { floatValue: number | undefined }) => {
    setAddChipsAmount(values.floatValue ?? 0);
  };

  // Replace the field with the strictly parsed clipboard amount. Without preventDefault
  // the browser also inserts the raw text at the caret, and the resulting
  // onValueChange overwrites the parsed value with the combined digits.
  const handlePasteValue = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    setAddChipsAmount(parsePastedAmount(event.clipboardData.getData('text')));
  };

  const isHalfDisabled = ((isPennyTable ? +half : Decimal.div(maximumTopUp, 2).floor().toNumber()) <
    tablelow) as boolean;

  const addOnFormContent = (
    <>
      {languageKey === 'GAME_MSG_BUSTED_PAUSE' && isTimerOff && (
        <>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <CountDownTimer isStartProgress={openAddChips} size={110} onComplete={onComplete} />
          </div>
          <div style={messageStyle}>
            {game.bust?.isBusted ? 'You ran out of chips. Add chips to rejoin the game.' : ''}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button style={presetBtnStyle} onClick={() => setAddChipsAmount(minAddChipsAmount)}>
          <span style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.45)', fontWeight: 600 }}>
            Min
          </span>
          <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 700 }}>
            $
            {maximumTopUp <= 0
              ? 0
              : isPennyTable
              ? numberFormatter(tablelow)
              : numberFormatter(Math.floor(tablelow))}
          </span>
        </button>
        <button
          style={isHalfDisabled ? disabledPresetBtnStyle : presetBtnStyle}
          disabled={isHalfDisabled}
          onClick={() =>
            setAddChipsAmount(
              isPennyTable ? +half : Decimal.div(maximumTopUp, 2).floor().toNumber()
            )
          }>
          <span style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.45)', fontWeight: 600 }}>
            1/2
          </span>
          <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 700 }}>
            ${isPennyTable ? half : Decimal.div(maximumTopUp, 2).floor().toNumber()}
          </span>
        </button>
        <button
          style={presetBtnStyle}
          onClick={() =>
            setAddChipsAmount(
              isPennyTable
                ? new Decimal(maximumTopUp).toDP(2).toNumber()
                : Decimal.floor(maximumTopUp).toNumber()
            )
          }>
          <span style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.45)', fontWeight: 600 }}>
            Max
          </span>
          <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 700 }}>
            $
            {isPennyTable
              ? new Decimal(maximumTopUp).toDP(2).toNumber()
              : Decimal.floor(maximumTopUp).toNumber()}
          </span>
        </button>
      </div>

      <div style={{ padding: '0 0.25rem' }}>
        <Slider
          aria-label='BUY-IN'
          defaultValue={tablelow}
          onChange={(e) => handleAddChipsSlider(e)}
          max={+maximumTopUp}
          min={tablelow}
          sx={{
            color: '#E98C26',
            '& .MuiSlider-thumb': { width: 20, height: 20, backgroundColor: '#d4a853' },
            '& .MuiSlider-rail': { backgroundColor: 'rgba(255,255,255,0.12)' }
          }}
          value={addChipsAmount}
          step={sliderStep}
        />
      </div>

      <NumericFormat
        customInput={AmountInput}
        value={addChipsAmount}
        onValueChange={handleAddChipsValue}
        onPaste={handlePasteValue}
        decimalScale={isPennyTable ? 2 : 0}
        allowNegative={false}
        prefix='$'
      />

      <div style={btnRowStyle}>
        <button style={secondaryBtnStyle} onClick={cancelAddChips}>
          Cancel
        </button>
        <button
          style={{
            ...primaryBtnStyle,
            ...(isAddChipsAmountInvalid ? { opacity: 0.4, pointerEvents: 'none' as const } : {})
          }}
          onClick={handleAddChips}
          // Block submission whenever the typed amount falls outside the Min/Max
          // window shown in the preset buttons above. addChipsAmount is the
          // single source of truth now that the value handler stores the raw
          // typed number as-is (no clamping in the handler).
          disabled={isAddChipsAmountInvalid}>
          Add Chips
        </button>
      </div>
    </>
  );

  const msgContent = (
    <>
      <div style={messageStyle}>You are only allowed to add chips once during the hand</div>
      <div style={btnRowStyle}>
        <button
          style={secondaryBtnStyle}
          onClick={() => {
            setCloseBuyInModal(false);
            setOpenAddChips(false);
          }}>
          Close
        </button>
      </div>
    </>
  );

  return (
    <GameModal
      open={openAddChips && !closeBuyInModal && isPlayerSeated}
      onClose={cancelAddChips}
      title='Add Chips'>
      {currentPlayerHasAddOnChips ? msgContent : addOnFormContent}
    </GameModal>
  );
};

export default AddChipsModal;
